// Classroom page (SPEC §7.7): name, center, capacity, note, weekly unavailability, periods, archive, delete.

import type { ApiResult } from "../../api/client";
import {
  addClassroomPeriod, deleteClassroom, fetchClassroom, removeClassroomPeriod, setClassroomArchived, updateClassroom,
} from "../../api/classrooms";
import { fetchCenters } from "../../api/centers";
import type { NewPeriod } from "../../api/teachers";
import type { Center, Classroom, Lesson } from "../../api/types";
import { formatWeekdayShort, t } from "../../i18n/i18n";
import { formatRange } from "../../time";
import { navigate, setRedraw, type RouteMatch } from "../../router";
import { isAdmin } from "../../session";
import { createConfirmButton } from "../../ui/confirmButton";
import { createButton, createElement } from "../../ui/dom";
import { createInputField, createSelectField, createTextAreaField, type Field } from "../../ui/forms";
import { buildAffectedLessons, buildPeriodsSection } from "../../ui/periodsSection";
import { reportAndReload } from "../../ui/resultMessage";
import { createWeeklyEditor, type WeeklyEditor } from "../../ui/weeklyEditor";
import { createLoading, showPageProblem, startPage } from "../pageFrame";

export async function renderClassroomPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("admin.classrooms"));
  page.append(createLoading());
  const loaded: ApiResult<Classroom> = await fetchClassroom(route.params.id ?? "");
  const centers: ApiResult<Center[]> = await fetchCenters(true);
  if (loaded.ok === false) {
    showPageProblem(page, loaded.problem);
    return;
  }

  let classroom: Classroom = loaded.data;
  let affected: Lesson[] | null = null;
  const draw = (): void => {
    const fresh: HTMLElement = startPage(container, classroom.name);
    drawPage(fresh, classroom, centers.ok ? centers.data : []);
    fresh.append(...buildPeriodParts(classroom, affected, async (lessons: Lesson[] | null): Promise<void> => {
      const reloaded: ApiResult<Classroom> = await fetchClassroom(classroom.id);
      classroom = reloaded.ok ? reloaded.data : classroom;
      affected = lessons;
      draw();
    }));
  };
  setRedraw(container, draw);
  draw();
}

function buildPeriodParts(classroom: Classroom, affected: Lesson[] | null,
  onChanged: (lessons: Lesson[] | null) => Promise<void>): HTMLElement[] {
  const parts: HTMLElement[] = affected === null ? [] : [buildAffectedLessons(affected)];
  parts.push(buildPeriodsSection({
    periods: classroom.unavailability,
    reasons: ["RoomClosed", "Holiday", "Other"],
    canEdit: isAdmin(),
    add: (period: NewPeriod) => addClassroomPeriod(classroom.id, period),
    remove: (periodId: string) => removeClassroomPeriod(classroom.id, periodId),
    onChanged: (lessons: Lesson[] | null): void => {
      void onChanged(lessons);
    },
  }));
  return parts;
}

function drawPage(page: HTMLElement, classroom: Classroom, centers: Center[]): void {
  const options: { value: string; label: string }[] = centers.map(
    (center: Center): { value: string; label: string } => ({ value: center.id, label: center.name }));
  const center: Field<HTMLSelectElement> = createSelectField(t("day.center"), options, classroom.centerId);
  const name: Field<HTMLInputElement> = createInputField(t("app.name"), "text", classroom.name);
  const capacity: Field<HTMLInputElement> = createInputField(t("admin.capacity"), "number",
    classroom.capacity === null ? "" : String(classroom.capacity));
  const note: Field<HTMLTextAreaElement> = createTextAreaField(t("app.note"), classroom.note ?? "");
  const weekly: WeeklyEditor = createWeeklyEditor(classroom.weeklyUnavailable);
  const grid: HTMLDivElement = createElement("div", "form-grid");
  grid.append(name.wrapper, center.wrapper, capacity.wrapper);
  const card: HTMLDivElement = createElement("div", "card");
  card.append(grid, note.wrapper, weekly.element);
  if (isAdmin() === false) {
    page.append(buildReadOnlyDetails(classroom, centers));
    return;
  }

  card.append(buildActions(classroom, (): Promise<ApiResult<Classroom>> => updateClassroom(classroom.id, {
    centerId: center.input.value, name: name.input.value,
    capacity: capacity.input.value === "" ? null : Number(capacity.input.value),
    note: note.input.value, weeklyUnavailable: weekly.getEntries(),
  }, classroom.version)));

  page.append(card);
}

function buildActions(classroom: Classroom, save: () => Promise<ApiResult<Classroom>>): HTMLElement {
  const actions: HTMLDivElement = createElement("div", "form-actions");
  actions.append(createButton(t("app.save"), async (): Promise<void> => {
    await reportAndReload(await save());
  }, "button-primary"), createButton(classroom.archived ? t("app.unarchive") : t("app.archive"),
    async (): Promise<void> => {
      await reportAndReload(await setClassroomArchived(classroom.id, classroom.archived === false,
        classroom.version));
    }), createConfirmButton(t("app.delete"), async (): Promise<void> => {
    const result: ApiResult<null> = await deleteClassroom(classroom.id);
    if (result.ok) {
      navigate("/admin/centers");
      return;
    }

    await reportAndReload(result);
  }));
  return actions;
}

/** What viewers see instead of the form (SPEC §7: forms are not rendered for read-only users). */
function buildReadOnlyDetails(classroom: Classroom, centers: Center[]): HTMLElement {
  const center: Center | undefined = centers.find((candidate: Center): boolean => candidate.id === classroom.centerId);
  const card: HTMLDivElement = createElement("div", "card");
  card.append(createElement("p", "", `${t("day.center")}: ${center?.name ?? t("app.none")}`),
    createElement("p", "", `${t("admin.capacity")}: ${classroom.capacity === null ? t("app.none")
      : String(classroom.capacity)}`));
  for (const entry of classroom.weeklyUnavailable) {
    card.append(createElement("p", "muted", `${formatWeekdayShort(entry.day)} ${formatRange(entry.range)} ${
      entry.note ?? ""}`));
  }

  if (classroom.note !== null) {
    card.append(createElement("p", "muted", classroom.note));
  }

  return card;
}
